/* eslint-disable react-hooks/exhaustive-deps */
import React, { useRef, useEffect, useState } from 'react';
import Image from 'react-bootstrap/Image';
import { IMAGES } from '../../../../constants/imagePath';
import Container from 'react-bootstrap/Container';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import Button from 'react-bootstrap/Button';
import Form from 'react-bootstrap/Form';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlay, faCircleXmark } from '@fortawesome/free-solid-svg-icons';
import * as Yup from 'yup';
import { useFormik } from 'formik';
import { toast } from 'react-toastify';
import { StepProps, StepThreeFormValues } from '../../../../redux/modules/auth/types';
import { setStepThreeData } from '../../../../redux/modules/auth/accountSetupSlice';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../../../redux/rootReducer';
import Stepper from './Stepper';

const MAX_LOGO_SIZE_BYTES = 1024 * 1024;

const StepThree: React.FC<StepProps> = ({ onNext, onBack }) => {
  const dispatch = useDispatch();
  const selectStepThreeData = useSelector((state: RootState) => state.accountSetup.stepThree);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [previewLogo, setPreviewLogo] = useState<string | null>(null);

  const StepThreeValidationSchema = Yup.object().shape({
    websiteURL: Yup.string().required('Website URL is required').url('Invalid URL'),
    logo: Yup.mixed().nullable(),
  });

  const formik = useFormik<StepThreeFormValues>({
    initialValues: {
      websiteURL: '',
      logo: null,
    },
    validationSchema: StepThreeValidationSchema,
    onSubmit: (values: StepThreeFormValues) => {
      dispatch(setStepThreeData(values));
      if (onNext) {
        onNext();
      }
    },
  });

  useEffect(() => {
    formik.setValues(selectStepThreeData);
    if (selectStepThreeData.logo instanceof File) {
      setPreviewLogo(URL.createObjectURL(selectStepThreeData.logo));
    }
  }, [selectStepThreeData]);

  const handleLogoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > MAX_LOGO_SIZE_BYTES) {
      toast.error('Business logo exceeds 1 MB. Please choose a business logo no larger than 1 MB.');
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }

    if (!file.type.startsWith('image/')) {
      toast.error('File is not an image. Please upload an image file.');
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }

    const objectUrl = URL.createObjectURL(file);
    setPreviewLogo(objectUrl);
    formik.setFieldValue('logo', file);
  };

  const handleRemoveLogo = () => {
    setPreviewLogo(null);
    formik.setFieldValue('logo', null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  return (
    <div>
      {/* // -------------------- 3 step -------------------------- */}
      <div className='signup-process'>
        <Container>
          <div className="content">
            <Image src={IMAGES.blueLogo} className='top-logo' />
            <div className="card">
              <h2 className='card-title'>Account Setup</h2>
              <Row className='justify-content-center'>
                <Col xl={10}>
                  <Stepper currentStep={3} />
                  {formik.touched.websiteURL && formik.touched.logo && Object.keys(formik.errors).length > 0 && (
                    <div className="error-box" style={{ display: formik.touched && Object.keys(formik.errors).length > 0 ? 'block' : 'none' }}>
                      <span style={{ fontWeight: 'bold' }}> {`There are ${Object.values(formik.errors).length} errors in your form.`}<br /> </span>
                      {Object.values(formik.errors).map((error, index) => (
                        <span key={index}>{error}<br /></span>
                      ))}
                    </div>
                  )}
                  <Form onSubmit={formik.handleSubmit}>
                    <div className="form-title">
                      <h2>Want to import your brand?</h2>
                      <p>Enter your organization's website URL and upload your business logo.</p>
                    </div>
                    <Row>
                      <Col xl={6} lg={6} md={6} sm={12}>
                        <Form.Group controlId="formBasicEmail" className='form-group'>
                          <Form.Label>Website URL</Form.Label>
                          <Form.Control type="text" placeholder="Enter"
                            {...formik.getFieldProps('websiteURL')}
                          />
                          <Form.Text className="text-danger error-text">
                            {formik.touched.websiteURL && formik.errors.websiteURL}
                          </Form.Text>
                        </Form.Group>
                      </Col>
                      <Col xl={6} lg={6} md={6} sm={12}>
                        <Form.Group className='form-group position-relative'>
                          <Form.Label>Business Logo</Form.Label>
                          <div className="position-relative">
                            <Form.Control
                              ref={fileInputRef}
                              type="file"
                              id="business-logo-file"
                              accept="image/*"
                              className="visually-hidden"
                              onChange={handleLogoChange}
                            />
                            <label htmlFor="business-logo-file" className="logo-dropzone">
                              <svg className="logo-dropzone-icon" viewBox="0 0 24 24" aria-hidden="true">
                                <path d="M12 16V4m0 0L7.5 8.5M12 4l4.5 4.5M5 14.5v3A2.5 2.5 0 0 0 7.5 20h9a2.5 2.5 0 0 0 2.5-2.5v-3" />
                              </svg>
                              <span className="logo-dropzone-action">
                                {previewLogo ? 'Edit Logo' : 'Upload logo'}
                              </span>
                              {previewLogo && (
                                <img
                                  className="logo-dropzone-preview"
                                  alt="Business logo preview"
                                  src={previewLogo}
                                />
                              )}
                            </label>
                            {previewLogo && (
                              <button
                                type="button"
                                className="logo-dropzone-remove"
                                aria-label="Remove business logo"
                                title="Remove Logo"
                                onClick={handleRemoveLogo}
                              >
                                <FontAwesomeIcon icon={faCircleXmark} />
                              </button>
                            )}
                          </div>
                          <Form.Text className='text-danger error-text'>
                            {formik.touched.logo && (formik.errors.logo as string)}
                          </Form.Text>
                        </Form.Group>
                      </Col>
                    </Row>
                    <div className='d-flex justify-content-between align-items-center'>
                      <Button variant="primary" className='btn-theme btn-border' onClick={onBack}>
                        <FontAwesomeIcon icon={faPlay} />Back
                      </Button>
                      <Button variant="primary" type="submit" className='btn-theme'>
                        Next<FontAwesomeIcon icon={faPlay} />
                      </Button>
                    </div>
                  </Form>
                </Col>

              </Row>


            </div>
          </div>
        </Container>
      </div>

    </div>

  )
}

export default StepThree;